import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  Timestamp,
  updateDoc,
  where,
  type DocumentData,
  type QuerySnapshot,
} from 'firebase/firestore';
import {
  refuelEventCostKsh,
  refuelEventLiters,
  refuelEventTimestampMs,
} from '../fleetRefuelUtils';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import {
  isCargoOrg,
  rawProductType,
} from '../fleetConstants';
import { fleetNavItems, fleetPortalTitle, getFleetCollections } from '../fleetPortal';
import { generateFleetSignupCode, getOrCreateFleetSignupCode } from '../generateFleetSignupCode';
import { materializeBusDoc, openLatLng } from '../../utils/locationCrypto';
import { useFleetAlerts } from '../useFleetAlerts';
import FleetIcon from './FleetIcon';
import {
  AlertsSidebar,
  ClientsListView,
  DriversListView,
  exportIncidentsMonthlyCsv,
  exportTripsCsv,
  FleetKpiRow,
  FleetSettingsView,
  FuelLogsView,
  IncidentsView,
  LiveMapSection,
  SignupCodeModal,
  OverviewFleetHistory,
  isCompletedFleetTrip,
  TripsAuditTable,
  VehicleDetailScreen,
  VehicleTable,
  type VehicleRow,
} from './LorryFleetConsoleViews';
import SubscriptionManager from '../../components/SubscriptionManager';
import '../../site-v3/legacy-v3.css'; // Barlow, the trasseapp.com face (2026-10-28)

type ActiveTrip = {
  id: string;
  cargoRef?: string;
  trackingToken?: string;
  origin?: string;
  destination?: string;
  status?: string;
};

const NAV_ICONS: Record<string, string> = {
  overview: 'gauge',
  live: 'map',
  vehicles: 'truck',
  cargoTrips: 'route',
  trips: 'route',
  drivers: 'user',
  clients: 'user',
  fuel: 'fuel',
  reports: 'flag',
  settings: 'gauge',
};

const ROUTE_COLORS = ['#00707F', '#1E7A4C', '#9A5B00', '#5D646B', '#B42318'];

function ratingFromEff(eff: number | null): string {
  if (eff == null) return '—';
  if (eff >= 4.2) return 'Excellent';
  if (eff >= 3.5) return 'Good';
  return 'Fair';
}

function formatPlate(id: string): string {
  const u = id.replace(/[\s-]/g, '').toUpperCase();
  if (u.length >= 7) return `${u.slice(0, 3)} ${u.slice(3, 6)} ${u.slice(6)}`;
  return id;
}

type LorryFleetConsoleProps = {
  productType: string;
};

export default function LorryFleetConsole({ productType }: LorryFleetConsoleProps) {
  const { fleetOrgCode, logout, currentUser } = useAuth();
  const isCargo = isCargoOrg(rawProductType(productType));
  const fleetCols = useMemo(() => getFleetCollections(productType), [productType]);
  const navItems = useMemo(() => fleetNavItems(productType), [productType]);

  const [nav, setNav] = useState('live');
  const [orgName, setOrgName] = useState('');
  const [vehicles, setVehicles] = useState<VehicleRow[]>([]);
  const { alerts: fleetAlerts, unreadCount: unreadAlerts, dismissAlert, dismissAllUnread } =
    useFleetAlerts(fleetOrgCode);
  const [activeTrips, setActiveTrips] = useState<ActiveTrip[]>([]);
  const [tripHistory, setTripHistory] = useState<Record<string, unknown>[]>([]);
  const [refuelEvents, setRefuelEvents] = useState<Record<string, unknown>[]>([]);
  const [issueReports, setIssueReports] = useState<Record<string, unknown>[]>([]);
  const [driverNames, setDriverNames] = useState<Record<string, string>>({});
  const [driverRows, setDriverRows] = useState<Array<{ uid: string; name: string; vehicleId?: string; isPrimary: boolean }>>([]);
  const [clientUsers, setClientUsers] = useState<{ id: string; name: string; email: string }[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [needsRefuelCount, setNeedsRefuelCount] = useState(0);
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [signupCode, setSignupCode] = useState<string | null>(null);
  const [signupExpiresAt, setSignupExpiresAt] = useState<number | null>(null);
  const [selectedVehicleId, setSelectedVehicleId] = useState<string | null>(null);
  const [vehicleDocs, setVehicleDocs] = useState<Record<string, Record<string, unknown>>>({});
  const [restByVehicleId, setRestByVehicleId] = useState<Record<string, { lat: number; lng: number }>>({});
  const [signupLoading, setSignupLoading] = useState(false);
  const [showAlertsFocus, setShowAlertsFocus] = useState(false);
  const [orgAddons, setOrgAddons] = useState<Record<string, { wanted?: boolean; status?: string; verified?: boolean }> | null>(null);
  const [showSubManager, setShowSubManager] = useState(false);
  const [orgDocData, setOrgDocData] = useState<Record<string, unknown> | null>(null);

  useEffect(() => {
    if (!fleetOrgCode) return;
    const q = query(collection(db, 'organizations'), where('code', '==', fleetOrgCode), limit(1));
    const unsub = onSnapshot(q, (snap) => {
      const d = snap.docs[0]?.data();
      setOrgName((d?.name as string) || fleetOrgCode);
      setOrgAddons((d?.addons as Record<string, { wanted?: boolean; status?: string }>) || null);
      if (d) {
        setOrgDocData(d as Record<string, unknown>);

        // Auto-apply pending plan change if effectiveDate has passed
        if ((d as Record<string, unknown>)?.pendingPlanChange) {
          const orgData = d as Record<string, unknown>;
          const pending = orgData.pendingPlanChange as Record<string, unknown>;
          const effectiveDate = new Date(pending.effectiveDate as string);
          if (effectiveDate <= new Date()) {
            const { plan, addons: pendingAddons } = pending as { plan: string; addons: Record<string, boolean> };
            const addonUpdates: Record<string, boolean | string> = {};
            Object.entries(pendingAddons || {}).forEach(([k, v]) => {
              addonUpdates[`addons.${k}.wanted`] = v as boolean;
              addonUpdates[`addons.${k}.status`] = (v as boolean) ? 'default' : 'off';
            });
            updateDoc(doc(db, 'organizations', snap.docs[0].id), {
              subscriptionPlan: plan,
              ...addonUpdates,
              pendingPlanChange: null,
              updatedAt: new Date(),
            }).catch(() => {});
          }
        }
      }
    });
    return () => unsub();
  }, [fleetOrgCode]);

  useEffect(() => {
    if (!fleetOrgCode) {
      setVehicles([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const q = query(
      collection(db, fleetCols.vehicles),
      where(fleetCols.orgField, '==', fleetOrgCode),
      where('isActive', '==', true)
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        void (async () => {
          const docs: Record<string, Record<string, unknown>> = {};
          const rows: VehicleRow[] = [];
          for (let i = 0; i < snap.docs.length; i++) {
            const docSnap = snap.docs[i];
            const materialized = await materializeBusDoc(
              docSnap.data() as Record<string, unknown>,
            );
            docs[docSnap.id] = materialized;
            const d = materialized;
            const route = d.currentRoute as Record<string, unknown> | undefined;
            const routeStatus = route?.status as string | undefined;
            const inMaint = d.maintenanceMode === true || d.status === 'maintenance';
            const status: VehicleRow['status'] = inMaint ? 'maint' : routeStatus === 'active' ? 'trip' : 'idle';
            const km30 = Number(d.monthlyDistanceKm ?? d.totalDistanceKm ?? 0);
            const eff = d.fuelEfficiencyKmPerL != null ? Number(d.fuelEfficiencyKmPerL) : null;
            const rating = ratingFromEff(eff);
            const cost = Number(d.monthlyFuelCost ?? 0);
            const routePoints = (d.routePoints as { lat?: number; lng?: number; gapBefore?: boolean; estimated?: boolean }[] | undefined)?.map((p) => ({
              lat: Number(p.lat),
              lng: Number(p.lng),
              gapBefore: p.gapBefore === true,
              estimated: p.estimated === true,
            })).filter((p) => !Number.isNaN(p.lat) && !Number.isNaN(p.lng)) ?? [];
            rows.push({
              id: docSnap.id,
              plate: formatPlate((d.numberPlate as string) || (d.nickname as string) || docSnap.id),
              type: (d.vehicleType as string) || (d.busType as string) || 'Lorry',
              driver: (d.driverName as string) || 'Unassigned',
              status,
              km30,
              eff,
              rating,
              cost,
              color: ROUTE_COLORS[i % ROUTE_COLORS.length],
              routePoints,
            });
          }
          setVehicles(rows);
          setVehicleDocs(docs);
          setLoading(false);
        })();
      },
      () => setLoading(false)
    );
    return () => unsub();
  }, [fleetOrgCode, fleetCols.orgField, fleetCols.vehicles]);

  useEffect(() => {
    if (!vehicles.length) {
      setRestByVehicleId({});
      return;
    }
    let cancelled = false;
    const loadRest = async () => {
      const next: Record<string, { lat: number; lng: number }> = {};
      for (const v of vehicles) {
        const raw = vehicleDocs[v.id];
        try {
          /* The org filter is NOT decoration — without it this query is denied
             (bug 77a). `lorryTrips` / `fleetTrips` authorise a read by
             comparing the document's OWN org field, and Firestore proves a list
             query against its potential result set rather than the rows it
             would return: a busId-only query can never demonstrate that every
             matchable trip belongs to this org, so it fails closed for every
             fleet_admin and owner. The catch below then blamed a missing index
             and quietly fell back to the vehicle's live position, which is why
             this looked like it worked. Same defect as bug 73 on the bus side.
             Needs the (org, busId, routeFinishedAt desc) composite index — both
             are in firestore.indexes.json. */
          const q = query(
            collection(db, fleetCols.trips),
            where(fleetCols.orgField, '==', fleetOrgCode),
            where('busId', '==', v.id),
            orderBy('routeFinishedAt', 'desc'),
            limit(1)
          );
          const snap = await getDocs(q);
          const d = snap.docs[0]?.data() as Record<string, unknown> | undefined;
          const lat = Number(d?.mapEndLiveLat ?? d?.mapEndLat);
          const lng = Number(d?.mapEndLiveLng ?? d?.mapEndLng);
          if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
            next[v.id] = { lat, lng };
            continue;
          }
        } catch (err) {
          // Never silent again: this catch spent months reporting a permission
          // denial as a missing index (bug 77a).
          console.error('[LorryFleetConsole] rest-position trip read failed', v.id, err);
        }
        const live = raw?.liveLocation as Record<string, unknown> | undefined;
        const opened = await openLatLng(live);
        if (opened) {
          next[v.id] = { lat: opened.lat, lng: opened.lng };
        }
      }
      if (!cancelled) setRestByVehicleId(next);
    };
    void loadRest();
    return () => {
      cancelled = true;
    };
  }, [vehicles, vehicleDocs, fleetCols.trips]);

  useEffect(() => {
    if (!fleetOrgCode) return;

    let schoolSnap: { docs: Array<{ id: string; data: () => Record<string, unknown> }> } | null = null;
    let orgSnap: { docs: Array<{ id: string; data: () => Record<string, unknown> }> } | null = null;

    const publish = () => {
      const nextMap: Record<string, string> = {};
      const nextRows: Array<{ uid: string; name: string; vehicleId?: string; isPrimary: boolean }> = [];
      const nextSeen = new Set<string>();
      const mergeSnap = (snap: typeof schoolSnap) => {
        if (!snap) return;
        snap.docs.forEach((d) => {
          if (nextSeen.has(d.id)) return;
          const data = d.data();
          const role = String(data.role ?? '').toLowerCase();
          if (role === 'fleet_admin' || role === 'parent' || role === 'client') return;
          if (role === 'owner') {
            const hasVehicle =
              Boolean((data.busId as string)?.trim()) || Boolean((data.vehicleId as string)?.trim());
            if (!hasVehicle) return;
          }
          if (role !== 'driver' && role !== 'bus_driver' && role !== 'owner' && !role.includes('driver')) {
            return;
          }
          nextSeen.add(d.id);
          const name = (data.displayName as string) || (data.email as string) || 'Driver';
          nextMap[d.id] = name;
          nextRows.push({
            uid: d.id,
            name,
            vehicleId: (data.busId as string) || (data.vehicleId as string) || undefined,
            isPrimary: data.isBusOwner === true || data.isVehicleOwner === true,
          });
        });
      };
      mergeSnap(schoolSnap);
      mergeSnap(orgSnap);
      setDriverNames(nextMap);
      setDriverRows(nextRows);
    };

    const qSchool = query(collection(db, 'users'), where('schoolCode', '==', fleetOrgCode));
    const qOrg = query(collection(db, 'users'), where('organizationCode', '==', fleetOrgCode));
    const unsubSchool = onSnapshot(qSchool, (snap) => {
      schoolSnap = snap;
      publish();
    });
    const unsubOrg = onSnapshot(qOrg, (snap) => {
      orgSnap = snap;
      publish();
    });
    return () => {
      unsubSchool();
      unsubOrg();
    };
  }, [fleetOrgCode]);

  useEffect(() => {
    if (!fleetOrgCode || !isCargo) {
      setActiveTrips([]);
      return;
    }
    const q = query(
      collection(db, fleetCols.trips),
      where('schoolCode', '==', fleetOrgCode),
      limit(24)
    );
    const unsub = onSnapshot(q, (snap) => {
      const trips = snap.docs
        .map((d) => {
          const data = d.data();
          const finished = data.routeFinishedAt;
          const status = data.status as string | undefined;
          if (finished || status === 'completed' || status === 'cancelled') return null;
          return {
            id: d.id,
            cargoRef: data.cargoRef as string | undefined,
            trackingToken: data.trackingToken as string | undefined,
            origin: data.origin as string | undefined,
            destination: data.destination as string | undefined,
            status: status || 'active',
            startedAt: (data.routeStartedAt as Timestamp | undefined)?.toDate?.()?.getTime() ?? 0,
          };
        })
        .filter(Boolean) as Array<ActiveTrip & { startedAt: number }>;
      trips.sort((a, b) => b.startedAt - a.startedAt);
      setActiveTrips(trips.map(({ startedAt: _s, ...t }) => t));
    }, () => setActiveTrips([]));
    return () => unsub();
  }, [fleetOrgCode, isCargo, fleetCols.trips]);

  useEffect(() => {
    if (!fleetOrgCode) {
      setTripHistory([]);
      setNeedsRefuelCount(0);
      return;
    }
    const col = collection(db, fleetCols.trips);
    const orgField = fleetCols.orgField;
    const publish = (snap: QuerySnapshot<DocumentData>) => {
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      rows.sort(
        (a, b) =>
          (((b as Record<string, unknown>).routeStartedAt as Timestamp | undefined)?.toMillis?.() ?? 0) -
          (((a as Record<string, unknown>).routeStartedAt as Timestamp | undefined)?.toMillis?.() ?? 0)
      );
      setTripHistory(rows);
      setNeedsRefuelCount(rows.filter((r) => (r as Record<string, unknown>).needsRefuelSoon === true).length);
    };
    let fallbackUnsub: (() => void) | undefined;
    const qTrips = query(
      col,
      where(orgField, '==', fleetOrgCode),
      orderBy('routeStartedAt', 'desc'),
      limit(120)
    );
    const unsub = onSnapshot(
      qTrips,
      publish,
      () => {
        const qFallback = query(col, where(orgField, '==', fleetOrgCode), limit(120));
        fallbackUnsub = onSnapshot(qFallback, publish, () => {
          setTripHistory([]);
          setNeedsRefuelCount(0);
        });
      }
    );
    return () => {
      unsub();
      fallbackUnsub?.();
    };
  }, [fleetOrgCode, fleetCols.trips, fleetCols.orgField]);

  const [refuelDrillOpen, setRefuelDrillOpen] = useState(false);

  useEffect(() => {
    if (!fleetOrgCode) {
      setRefuelEvents([]);
      return;
    }
    const col = collection(db, fleetCols.refuelEvents);
    const orgField = fleetCols.orgField;
    const qRefuels = query(
      col,
      where(orgField, '==', fleetOrgCode),
      orderBy('createdAt', 'desc'),
      limit(500)
    );
    const publish = (snap: QuerySnapshot<DocumentData>) => {
      const byId = new Map<string, Record<string, unknown>>();
      snap.docs.forEach((d) => byId.set(d.id, { id: d.id, ...d.data() }));
      const rows = [...byId.values()];
      rows.sort(
        (a, b) =>
          refuelEventTimestampMs(b as Record<string, unknown>) -
          refuelEventTimestampMs(a as Record<string, unknown>)
      );
      setRefuelEvents(rows);
    };

    let fallbackUnsub: (() => void) | undefined;
    const unsub = onSnapshot(
      qRefuels,
      publish,
      () => {
        const qFallback = query(col, where(orgField, '==', fleetOrgCode), limit(500));
        fallbackUnsub = onSnapshot(qFallback, publish, () => setRefuelEvents([]));
      }
    );
    return () => {
      unsub();
      fallbackUnsub?.();
    };
  }, [fleetOrgCode, fleetCols.refuelEvents, fleetCols.orgField]);

  useEffect(() => {
    if (!fleetOrgCode || Object.keys(vehicleDocs).length === 0) return;

    const assignByUid = new Map<string, { vehicleId: string; isPrimary: boolean }>();
    Object.entries(vehicleDocs).forEach(([vehicleId, d]) => {
      const ownerId = String(d.ownerId ?? '').trim();
      const driverIds = Array.isArray(d.driverIds) ? (d.driverIds as string[]) : [];
      if (ownerId) {
        assignByUid.set(ownerId, { vehicleId, isPrimary: true });
      }
      driverIds.forEach((uid) => {
        if (!uid) return;
        if (!assignByUid.has(uid)) {
          assignByUid.set(uid, { vehicleId, isPrimary: false });
        }
      });
    });
    if (assignByUid.size === 0) return;

    let cancelled = false;
    void (async () => {
      const extra: Array<{ uid: string; name: string; vehicleId?: string; isPrimary: boolean }> = [];
      for (const [uid, assign] of assignByUid) {
        try {
          const snap = await getDoc(doc(db, 'users', uid));
          if (!snap.exists()) {
            extra.push({ uid, name: 'Driver', ...assign });
            continue;
          }
          const data = snap.data() as Record<string, unknown>;
          const name =
            (data.displayName as string) || (data.name as string) || (data.email as string) || 'Driver';
          extra.push({
            uid,
            name,
            vehicleId: assign.vehicleId,
            isPrimary: assign.isPrimary || data.isVehicleOwner === true || data.isBusOwner === true,
          });
        } catch (_) {
          extra.push({ uid, name: 'Driver', ...assign });
        }
      }
      if (cancelled) return;
      setDriverRows((prev) => {
        const byUid = new Map(prev.map((r) => [r.uid, r]));
        extra.forEach((r) => {
          const existing = byUid.get(r.uid);
          byUid.set(r.uid, existing ? { ...existing, ...r, name: r.name || existing.name } : r);
        });
        return [...byUid.values()].sort((a, b) => a.name.localeCompare(b.name));
      });
      setDriverNames((prev) => {
        const next = { ...prev };
        extra.forEach((r) => {
          next[r.uid] = r.name;
        });
        return next;
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [fleetOrgCode, vehicleDocs]);

  useEffect(() => {
    if (!fleetOrgCode) {
      setIssueReports([]);
      return;
    }
    /* Query shape has to match the RULE, not the data model (bug 77b).
     *
     * This asked for issue reports by org field — `where('schoolCode','==',org)`
     * and friends. But `lorryIssueReports` (like `busIssueReports`) authorises a
     * row by resolving `get(lorries/{busId})` and comparing THAT document's org,
     * because the report itself carries no trustworthy org stamp. Firestore
     * proves a list query against its potential result set, so an org-field
     * query can never satisfy a get()-based rule: denied in full, and
     * `.catch(() => setIssueReports([]))` rendered the denial as "no reports".
     *
     * So query the way the rule can prove: by `busId in [...]`, using REAL
     * vehicle doc ids only. Each `in` value is proven separately and one
     * unresolvable id denies the whole query — the exact trap that cost the
     * bus side an afternoon on 2026-08-04 — which is why the ids come straight
     * off the loaded vehicle docs and are never expanded into plate variants.
     * Chunks of 8, not 30, for the undocumented per-query access-call budget. */
    const vehicleIds = [...new Set(vehicles.map((v) => v.id).filter(Boolean))];
    if (!vehicleIds.length) {
      setIssueReports([]);
      return;
    }
    /* SELF_TRACKING orgs keep vehicles in `fleetVehicles` but file reports into
     * `busIssueReports`, whose rule resolves `get(buses/{busId})` — a document
     * that does not exist for them. No query shape can pass that, so there is
     * nothing to fix here: skip the read rather than ship a guaranteed denial,
     * and leave the list honestly empty. The real fix is a rules change (bug
     * 77c) and it needs deciding, not guessing — a get() budget is shared
     * across `in` values and the bus path that works today runs through the
     * same function. */
    if (fleetCols.vehicles === 'fleetVehicles' && fleetCols.issueReports === 'busIssueReports') {
      setIssueReports([]);
      return;
    }
    const col = collection(db, fleetCols.issueReports);
    const chunks: string[][] = [];
    for (let i = 0; i < vehicleIds.length; i += 8) chunks.push(vehicleIds.slice(i, i + 8));
    Promise.all(
      chunks.map((chunk) =>
        // orgCode filter is required by the rule — see the note in BusTracking.tsx.
        getDocs(query(col, where('orgCode', '==', fleetOrgCode), where('busId', 'in', chunk), limit(120))).catch((err) => {
          console.error('[LorryFleetConsole] issue-report read failed for chunk', chunk, err);
          return null;
        })
      )
    )
      .then((snaps) => {
        const merged = new Map<string, Record<string, unknown>>();
        for (const snap of snaps) {
          if (!snap) continue;
          for (const d of snap.docs) {
            merged.set(d.id, { id: d.id, ...d.data() });
          }
        }
        const rows = [...merged.values()];
        rows.sort(
          (a, b) =>
            refuelEventTimestampMs(b as Record<string, unknown>) -
            refuelEventTimestampMs(a as Record<string, unknown>)
        );
        setIssueReports(rows);
      })
      .catch(() => setIssueReports([]));
  }, [fleetOrgCode, fleetCols.issueReports, fleetCols.orgField, fleetCols.vehicles, vehicles]);

  useEffect(() => {
    if (!fleetOrgCode || !isCargo) {
      setClientUsers([]);
      return;
    }
    const q = query(collection(db, 'users'), where('schoolCode', '==', fleetOrgCode));
    const unsub = onSnapshot(q, (snap) => {
      setClientUsers(
        snap.docs
          .filter((d) => {
            const r = String(d.data().role ?? '');
            return r === 'client' || r === 'parent';
          })
          .map((d) => ({
            id: d.id,
            name: (d.data().displayName as string) || (d.data().name as string) || 'Client',
            email: (d.data().email as string) || '',
          }))
      );
    });
    return () => unsub();
  }, [fleetOrgCode, isCargo]);

  const filteredVehicles = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return vehicles;
    return vehicles.filter(
      (v) => v.plate.toLowerCase().includes(q) || v.driver.toLowerCase().includes(q) || v.type.toLowerCase().includes(q)
    );
  }, [vehicles, search]);

  const thirtyAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;

  const liveCount = vehicles.filter((v) => v.status === 'trip').length;
  const vehicleKm30 = vehicles.reduce((s, v) => s + v.km30, 0);
  const avgEff =
    vehicles.filter((v) => v.eff != null).length > 0
      ? vehicles.reduce((s, v) => s + (v.eff ?? 0), 0) / vehicles.filter((v) => v.eff != null).length
      : 0;
  const fuelSpendFromRefuels = refuelEvents
    .filter((e) => refuelEventTimestampMs(e as Record<string, unknown>) >= thirtyAgo)
    .reduce((s, e) => s + refuelEventCostKsh(e as Record<string, unknown>), 0);
  const fuelSpend30 =
    fuelSpendFromRefuels > 0 ? fuelSpendFromRefuels : vehicles.reduce((s, v) => s + v.cost, 0);

  const initials = (currentUser?.email?.slice(0, 2) || 'FA').toUpperCase();
  const portalSubtitle = isCargo ? 'Cargo fleet' : 'Personal tracking';

  const NAV_TITLES: Record<string, string> = {
    overview: 'Overview',
    live: 'Live fleet',
    cargoTrips: 'Cargo trips',
    trips: 'Trips',
    vehicles: 'Vehicles',
    vehicle_detail: 'Vehicle detail',
    drivers: 'Drivers',
    clients: 'Clients',
    fuel: 'Fuel logs',
    reports: 'Reports',
    settings: 'Settings',
  };

  const refuel30 = refuelEvents.filter(
    (e) => refuelEventTimestampMs(e as Record<string, unknown>) >= thirtyAgo
  );
  const refuelLitres30 = refuel30.reduce(
    (s, e) => s + refuelEventLiters(e as Record<string, unknown>),
    0
  );

  const tripKm30 = tripHistory
    .filter((r) => {
      const row = r as Record<string, unknown>;
      if (!isCompletedFleetTrip(row)) return false;
      const ts =
        (row.routeFinishedAt as Timestamp | undefined)?.toDate?.()?.getTime() ??
        (row.routeStartedAt as Timestamp | undefined)?.toDate?.()?.getTime() ??
        0;
      return ts >= thirtyAgo;
    })
    .reduce((s, r) => s + Number((r as Record<string, unknown>).distanceKm ?? 0), 0);
  const totalKm30 = tripKm30 > 0 ? tripKm30 : vehicleKm30;

  const selectedVehicle = selectedVehicleId ? vehicles.find((v) => v.id === selectedVehicleId) : null;
  const selectedRaw = selectedVehicleId ? vehicleDocs[selectedVehicleId] : undefined;

  const handleRegisterVehicle = useCallback(async () => {
    if (!fleetOrgCode || !currentUser?.uid) return;
    setSignupLoading(true);
    try {
      const { code, expiresAt } = await getOrCreateFleetSignupCode(fleetOrgCode, currentUser.uid);
      setSignupCode(code);
      setSignupExpiresAt(expiresAt);
      setShowSignupModal(true);
    } finally {
      setSignupLoading(false);
    }
  }, [fleetOrgCode, currentUser?.uid]);

  const openVehicleDetail = useCallback((id: string) => {
    setSelectedVehicleId(id);
    setNav('vehicle_detail');
  }, []);

  const saveVehicleDetail = useCallback(async (tankInput: string, benchInput: string) => {
    if (!selectedVehicleId) return;
    const updates: {
      updatedAt: ReturnType<typeof serverTimestamp>;
      fuelTankCapacityL?: number;
      benchmarkKmPerLiter?: number;
      benchmarkKmPerL?: number;
    } = { updatedAt: serverTimestamp() };
    const tank = parseFloat(tankInput);
    if (!Number.isNaN(tank) && tank >= 50 && tank <= 500) updates.fuelTankCapacityL = tank;
    const bench = parseFloat(benchInput);
    if (!Number.isNaN(bench) && bench > 0) {
      updates.benchmarkKmPerLiter = bench;
      updates.benchmarkKmPerL = bench;
    }
    await updateDoc(doc(db, fleetCols.vehicles, selectedVehicleId), updates);
  }, [selectedVehicleId, fleetCols.vehicles]);

  const toggleMaintenance = useCallback(async () => {
    if (!selectedVehicleId || !selectedVehicle) return;
    const next = selectedVehicle.status !== 'maint';
    await updateDoc(doc(db, fleetCols.vehicles, selectedVehicleId), {
      maintenanceMode: next,
      status: next ? 'maintenance' : 'idle',
      updatedAt: serverTimestamp(),
    });
  }, [selectedVehicleId, selectedVehicle, fleetCols.vehicles]);

  const handleGenerateSignup = useCallback(async () => {
    if (!fleetOrgCode || !currentUser?.uid) return;
    setSignupLoading(true);
    try {
      const { code, expiresAt } = await generateFleetSignupCode(fleetOrgCode, currentUser.uid);
      setSignupCode(code);
      setSignupExpiresAt(expiresAt);
    } finally {
      setSignupLoading(false);
    }
  }, [fleetOrgCode, currentUser?.uid]); // Regenerate always creates a new code

  const copyTrackLink = (token: string) => {
    const url = `${window.location.origin}/track/${token}`;
    navigator.clipboard.writeText(url).catch(() => {});
  };

  return (
    <div style={{ display: 'flex', height: '100vh', background: '#F6F5F1', fontFamily: 'Barlow, system-ui, sans-serif', color: '#0F1419' }}>
      <div style={{ width: 230, flexShrink: 0, background: '#0F1419', display: 'flex', flexDirection: 'column', padding: '22px 14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '0 8px 22px' }}>
          <div style={{ width: 34, height: 34, borderRadius: 10, background: '#00707F', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <FleetIcon name="truck" size={18} color="#fff" />
          </div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#fff', letterSpacing: 0.5 }}>LORRY</div>
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', letterSpacing: 0.5 }}>Fleet console</div>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {navItems.map((n) => (
            <button
              key={n.id}
              type="button"
              onClick={() => setNav(n.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 11,
                padding: '10px 12px',
                borderRadius: 11,
                border: 'none',
                cursor: 'pointer',
                background: nav === n.id ? 'rgba(0,112,127,0.18)' : 'transparent',
                color: nav === n.id ? '#fff' : 'rgba(255,255,255,0.6)',
                fontSize: 13.5,
                fontWeight: nav === n.id ? 600 : 500,
                fontFamily: 'inherit',
                textAlign: 'left',
              }}
            >
              <FleetIcon name={NAV_ICONS[n.id] || 'gauge'} size={17} color={nav === n.id ? '#22D6F0' : 'rgba(255,255,255,0.55)'} />
              {n.label}
            </button>
          ))}
        </div>
        <div style={{ marginTop: 'auto', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 14, padding: '12px 13px' }}>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)' }}>{fleetPortalTitle(productType)}</div>
          <div style={{ fontSize: 12.5, color: '#fff', fontWeight: 600, marginTop: 3 }}>
            {vehicles.length} vehicle{vehicles.length !== 1 ? 's' : ''} · {liveCount} live
          </div>
          <div style={{ marginTop: 8, height: 5, background: 'rgba(255,255,255,0.12)', borderRadius: 3, overflow: 'hidden' }}>
            <div style={{ width: `${Math.min(100, vehicles.length ? (liveCount / vehicles.length) * 100 : 0)}%`, height: '100%', background: '#00707F' }} />
          </div>
        </div>
      </div>

      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ height: 64, flexShrink: 0, background: '#fff', borderBottom: '1px solid #E4E3DE', display: 'flex', alignItems: 'center', padding: '0 24px', gap: 16 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700 }}>{NAV_TITLES[nav] || 'Fleet'}</div>
            <div style={{ fontSize: 11.5, color: '#5D646B' }}>
              {orgName} · {portalSubtitle}
            </div>
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
            <button
              onClick={() => setShowSubManager(true)}
              style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', fontSize: 12, fontWeight: 600, color: '#3b5bdb', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}
            >
              ↑ Subscription
            </button>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#F6F5F1', border: '1px solid #E4E3DE', borderRadius: 11, padding: '9px 13px', width: 240 }}>
              <FleetIcon name="route" size={15} color="#8C9196" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search vehicle, driver…"
                style={{ border: 'none', background: 'transparent', outline: 'none', fontSize: 13, color: '#0F1419', width: '100%', fontFamily: 'inherit' }}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, background: '#F6F5F1', border: '1px solid #E4E3DE', borderRadius: 11, padding: '9px 13px', fontSize: 13, fontWeight: 600 }}>
              <FleetIcon name="clock" size={15} color="#5D646B" /> Last 30 days <FleetIcon name="chevDown" size={14} color="#8C9196" />
            </div>
            <button
              type="button"
              title="Fleet settings"
              onClick={() => setNav('settings')}
              style={{ width: 40, height: 40, borderRadius: 11, background: '#F6F5F1', border: '1px solid #E4E3DE', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
            >
              <FleetIcon name="gauge" size={17} color="#0F1419" />
            </button>
            <button
              type="button"
              onClick={() => {
                setShowAlertsFocus((v) => !v);
                if (nav !== 'live' && nav !== 'overview') setNav('live');
              }}
              style={{ position: 'relative', width: 40, height: 40, borderRadius: 11, background: '#F6F5F1', border: '1px solid #E4E3DE', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
            >
              <FleetIcon name="bell" size={17} color="#0F1419" />
              {unreadAlerts > 0 && (
                <div style={{ position: 'absolute', top: -3, right: -3, minWidth: 17, height: 17, padding: '0 4px', borderRadius: 9, background: '#B42318', color: '#fff', fontSize: 9.5, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid #fff' }}>
                  {unreadAlerts}
                </div>
              )}
            </button>
            <div style={{ width: 40, height: 40, borderRadius: 11, background: '#FFCBA4', color: '#fff', fontWeight: 700, fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {initials}
            </div>
          </div>
        </div>

        {/* Add-on connection notice — same logic as Bus Tracking */}
        {orgAddons && (() => {
          const labels: Record<string, string> = { gps: 'GPS sensor', fuel: 'Fuel sensor', fees: 'School fees module' };
          const pending = (['gps', 'fuel'] as const).filter(
            (k) => orgAddons[k]?.wanted && !(orgAddons[k]?.status === 'connected' && orgAddons[k]?.verified)
          );
          if (!pending.length) return null;
          return (
            <div style={{ padding: '8px 24px', background: '#FBEFD9', borderBottom: '1px solid #F3D9A4', display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#9A5B00' }}>
              <span style={{ color: '#f59e0b' }}>⚠</span>
              <b>Using default app values</b>
              <span style={{ color: '#a16207' }}>— {pending.map((k) => labels[k]).join(', ')} not connected. Ask your PEAR tech team to link them from the admin console.</span>
            </div>
          );
        })()}

        <div style={{ flex: 1, overflow: 'auto', padding: '20px 24px 28px' }}>
          {loading && nav !== 'vehicle_detail' && <div style={{ padding: 24, color: '#5D646B' }}>Loading fleet…</div>}

          {nav === 'vehicle_detail' && selectedVehicle && selectedRaw && (
            <VehicleDetailScreen
              vehicle={selectedVehicle}
              raw={selectedRaw}
              isCargo={isCargo}
              tripHistory={tripHistory.filter(
                (t) => String((t as Record<string, unknown>).busId ?? (t as Record<string, unknown>).lorryId) === selectedVehicle.id
              )}
              refuelEvents={refuelEvents.filter(
                (e) => String((e as Record<string, unknown>).busId ?? (e as Record<string, unknown>).lorryId) === selectedVehicle.id
              )}
              issueReports={issueReports.filter(
                (i) => String((i as Record<string, unknown>).busId ?? (i as Record<string, unknown>).lorryId) === selectedVehicle.id
              )}
              driverRows={driverRows}
              restLocation={restByVehicleId[selectedVehicle.id] ?? null}
              onBack={() => {
                setNav('vehicles');
                setSelectedVehicleId(null);
              }}
              onSave={saveVehicleDetail}
              onToggleMaint={toggleMaintenance}
            />
          )}

          {(nav === 'overview' || nav === 'live') && (
            <FleetKpiRow
              totalKm30={totalKm30}
              liveCount={liveCount}
              vehicleCount={vehicles.length}
              avgEff={avgEff}
              fuelSpend30={fuelSpend30}
              needsRefuelCount={needsRefuelCount}
              refuelLitres30={refuelLitres30}
              refuelCount30={refuel30.length}
              onRefuelAuditClick={() => setRefuelDrillOpen(true)}
            />
          )}

          {nav === 'overview' && (
            <>
              <div style={{ marginTop: 14, maxWidth: 420, marginLeft: 'auto' }}>
                <AlertsSidebar alerts={fleetAlerts} onDismiss={dismissAlert} onDismissAll={dismissAllUnread} />
              </div>
              <div style={{ marginTop: 14 }}>
                <OverviewFleetHistory
                  trips={tripHistory}
                  refuelEvents={refuelEvents}
                  vehicles={vehicles}
                  driverNames={driverNames}
                  formatPlate={formatPlate}
                  onExportTripsCsv={() => exportTripsCsv(tripHistory, formatPlate, vehicles)}
                />
              </div>
              <div style={{ marginTop: 14 }}>
                <VehicleTable vehicles={filteredVehicles} driverNames={driverNames} onRegister={handleRegisterVehicle} onSelect={openVehicleDetail} showRegister />
              </div>
            </>
          )}

          {nav === 'live' && (
            <>
              {isCargo && activeTrips.length > 0 && (
                <div style={{ background: '#fff', border: '1px solid #E4E3DE', borderRadius: 18, marginTop: 14, padding: '14px 16px' }}>
                  <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 10 }}>Active cargo trips · client tracking links</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {activeTrips.map((t) => (
                      <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', background: '#FAF9F6', border: '1px solid #EFEDE7', borderRadius: 13 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600 }}>{t.cargoRef || t.id}</div>
                          <div style={{ fontSize: 11, color: '#5D646B', marginTop: 2 }}>
                            {t.origin || 'Origin'} → {t.destination || 'Destination'}
                          </div>
                        </div>
                        {t.trackingToken ? (
                          <button type="button" onClick={() => copyTrackLink(t.trackingToken!)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px', background: '#00707F', color: '#fff', border: 'none', borderRadius: 10, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                            <FleetIcon name="share" size={14} color="#fff" /> Copy track link
                          </button>
                        ) : (
                          <span style={{ fontSize: 11, color: '#8C9196' }}>Link pending</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: showAlertsFocus ? '1fr 300px' : '1fr', gap: 14, marginTop: 14 }}>
                <LiveMapSection
                  vehicles={vehicles}
                  vehicleDocs={vehicleDocs}
                  restByVehicleId={restByVehicleId}
                  liveCount={liveCount}
                  orgSubtitle={portalSubtitle}
                />
                {showAlertsFocus && (
                  <AlertsSidebar alerts={fleetAlerts} onDismiss={dismissAlert} onDismissAll={dismissAllUnread} />
                )}
              </div>
              <div style={{ marginTop: 14 }}>
                <VehicleTable vehicles={filteredVehicles} driverNames={driverNames} onRegister={handleRegisterVehicle} onSelect={openVehicleDetail} />
              </div>
            </>
          )}

          {(nav === 'cargoTrips' || nav === 'trips') && (
            <div style={{ marginTop: 14 }}>
              <TripsAuditTable
                trips={tripHistory}
                vehicles={vehicles}
                formatPlate={formatPlate}
                driverNames={driverNames}
                onExportCsv={() => exportTripsCsv(tripHistory, formatPlate, vehicles)}
              />
            </div>
          )}

          {nav === 'vehicles' && (
            <div style={{ marginTop: 14 }}>
              <VehicleTable vehicles={filteredVehicles} driverNames={driverNames} onRegister={handleRegisterVehicle} onSelect={openVehicleDetail} />
            </div>
          )}

          {nav === 'drivers' && <div style={{ marginTop: 14 }}><DriversListView drivers={driverRows} /></div>}
          {nav === 'clients' && <div style={{ marginTop: 14 }}><ClientsListView clients={clientUsers} /></div>}
          {nav === 'fuel' && <div style={{ marginTop: 14 }}><FuelLogsView events={refuelEvents} /></div>}
          {nav === 'reports' && (
            <div style={{ marginTop: 14 }}>
              <IncidentsView issues={issueReports} vehicles={vehicles} formatPlate={formatPlate} onExportCsv={() => exportIncidentsMonthlyCsv(issueReports, formatPlate)} />
            </div>
          )}
          {nav === 'settings' && (
            <FleetSettingsView
              orgName={orgName}
              orgCode={fleetOrgCode ?? ''}
              email={currentUser?.email}
              onSignOut={() => logout()}
            />
          )}
        </div>

        {refuelDrillOpen && (
          <div
            role="dialog"
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(15,20,25,0.45)',
              zIndex: 1000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 24,
            }}
            onClick={() => setRefuelDrillOpen(false)}
          >
            <div
              style={{
                background: '#fff',
                borderRadius: 18,
                maxWidth: 640,
                width: '100%',
                maxHeight: '80vh',
                overflow: 'auto',
                padding: 20,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                <div style={{ fontSize: 16, fontWeight: 700 }}>Refuel audit · 30 days</div>
                <button type="button" onClick={() => setRefuelDrillOpen(false)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 18 }}>×</button>
              </div>
              <FuelLogsView events={refuel30} />
            </div>
          </div>
        )}

        <SignupCodeModal
          open={showSignupModal}
          code={signupCode}
          expiresAt={signupExpiresAt}
          loading={signupLoading}
          onGenerate={handleGenerateSignup}
          onClose={() => setShowSignupModal(false)}
        />

        {showSubManager && (
          <SubscriptionManager
            orgCode={fleetOrgCode ?? ''}
            orgData={orgDocData as Parameters<typeof SubscriptionManager>[0]['orgData']}
            onClose={() => setShowSubManager(false)}
          />
        )}
      </div>
    </div>
  );
}
